/**
 * A snapshot is a route plus the landmarks the Worker produced for it, so a drive can be
 * re-run and shared without the Worker (or the token) at hand.
 */
import type { FeatureFixture, TrackPoint, WarmedLandmark } from '@roadtrip/core';

export interface Snapshot {
  name: string;
  /** How the track was made: routed shapes are re-timed when the speed changes; GPX keeps its own timing. */
  source: 'routed' | 'gpx';
  speedMps: number;
  track: TrackPoint[];
  landmarks: WarmedLandmark[];
  /** Rivers, forests and wilderness with the shape each corridor call sent; absent from older snapshots. */
  features?: FeatureFixture;
  savedAt?: string;
}

export function downloadSnapshot(s: Snapshot): void {
  const blob = new Blob([JSON.stringify({ ...s, savedAt: new Date().toISOString() })], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${s.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'route'}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function isSnapshot(x: unknown): x is Snapshot {
  const s = x as Snapshot;
  return !!s && typeof s.name === 'string' && Array.isArray(s.track) && Array.isArray(s.landmarks) && typeof s.speedMps === 'number';
}

export async function readSnapshotFile(file: File): Promise<Snapshot> {
  const parsed: unknown = JSON.parse(await file.text());
  if (!isSnapshot(parsed)) throw new Error('not a Route Lab snapshot');
  return parsed;
}

export async function fetchSnapshot(url: string): Promise<Snapshot | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const parsed: unknown = await res.json();
    return isSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
